import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { VerificationDocType } from '@/lib/verification/docTypeLabels';
import { getRequiredDocs, getOptionalDocs } from '@/lib/verification/requiredDocs';

export interface VerificationDocument {
  id: string;
  company_id: string;
  doc_type: VerificationDocType;
  storage_path: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  parsed_fields: any;
  parse_confidence: number | null;
  match_results: any;
  discrepancies: any;
  document_expiry_date: string | null;
  review_status:
    | 'pending_parse'
    | 'auto_approved'
    | 'in_review'
    | 'approved'
    | 'rejected'
    | 'expired';
  reviewer_notes: string | null;
  reviewed_at: string | null;
  created_at: string;
  superseded_by: string | null;
}

export function useCompanyVerification(companyId: string | null, role: string | null) {
  const { user } = useAuth();
  const [documents, setDocuments] = useState<VerificationDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState<VerificationDocType | null>(null);

  const requiredDocs = getRequiredDocs(role);
  const optionalDocs = getOptionalDocs(role);
  const allDocTypes = [...requiredDocs, ...optionalDocs];

  const refresh = useCallback(async () => {
    if (!companyId) {
      setDocuments([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data } = await supabase
      .from('company_verification_documents' as any)
      .select('*')
      .eq('company_id', companyId)
      .is('superseded_by', null)
      .order('created_at', { ascending: false });
    setDocuments((data || []) as unknown as VerificationDocument[]);
    setLoading(false);
  }, [companyId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const latestByType = (docType: VerificationDocType) =>
    documents.find((d) => d.doc_type === docType) || null;

  const uploadDocument = useCallback(
    async (
      docType: VerificationDocType,
      file: File,
    ): Promise<{ error?: string; document?: VerificationDocument }> => {
      if (!companyId || !user) return { error: 'Not authorized' };
      if (file.size > 20 * 1024 * 1024) return { error: 'File too large (20MB max)' };

      setUploading(docType);
      try {
        const ext = file.name.split('.').pop() || 'pdf';
        const path = `${companyId}/${docType}/${Date.now()}.${ext}`;

        const { error: upErr } = await supabase.storage
          .from('compliance-documents')
          .upload(path, file, { upsert: false, contentType: file.type });
        if (upErr) return { error: upErr.message };

        // Mark prior doc of same type as superseded
        const prior = latestByType(docType);

        const { data: inserted, error: insErr } = await supabase
          .from('company_verification_documents' as any)
          .insert({
            company_id: companyId,
            uploaded_by: user.id,
            doc_type: docType,
            storage_path: path,
            file_name: file.name,
            file_size: file.size,
            mime_type: file.type,
            review_status: 'pending_parse',
          } as any)
          .select()
          .single();

        if (insErr || !inserted) return { error: insErr?.message || 'Insert failed' };

        if (prior) {
          await supabase
            .from('company_verification_documents' as any)
            .update({ superseded_by: (inserted as any).id } as any)
            .eq('id', prior.id);
        }

        // Plan A speed: grant posting rights immediately on upload; AI parsing
        // continues async. Idempotent — never re-enables admin-revoked verification.
        await supabase.rpc('mark_company_documents_submitted' as any, { _company_id: companyId });

        // Kick parser (fire & forget — UI polls via refresh)
        supabase.functions.invoke('parse-verification-document', {
          body: { document_id: (inserted as any).id },
        }).catch(() => {});

        await refresh();
        return { document: inserted as unknown as VerificationDocument };
      } finally {
        setUploading(null);
      }
    },
    [companyId, user, refresh, documents],
  );

  // Verification is satisfied when every required doc is approved or auto_approved
  const isVerified = requiredDocs.every((t) => {
    const d = latestByType(t);
    return d && (d.review_status === 'approved' || d.review_status === 'auto_approved');
  });

  return {
    documents,
    loading,
    uploading,
    requiredDocs,
    optionalDocs,
    allDocTypes,
    latestByType,
    uploadDocument,
    refresh,
    isVerified,
  };
}
